import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './Prova.styles'
import ModelProvaActions from './ModelProvaActions';

import { rebuildPosicions } from '../../../utils/loadPositions';
import { fetchAPI, postAPI } from '../../../utils/utils';
import { postOrderEdit } from './orderEdits';
import { applyTimeZone } from '../../../utils/utils';
import { can, isHeadTecnica, isTecnica } from '../../../utils/permissions';
import { getCastellerDisplayName } from '../../../utils/castellerName';

function Prova(props) {
    const { editing, allPosicions, setAllPosicions, bundleInfo, order, setOrder, setCaixesCastellers, castellersInfo, userInfo, socket, prova, setProves, event, isPrivate, isAdmin, duplicate, hores, absoluteHores, assistencies, labels={}, setLabels, feta = false, onToggleFeta = null } = props;
    const castellName = bundleInfo?.nom
    const castellShortName = bundleInfo?.shortName
    const castellDisplayName = castellShortName || castellName
    const privateLabel = isTecnica(userInfo) ? labels?.[prova] || '' : ''

    const canEditProva = can(userInfo, 'EDIT_PROVA');
    const canDeleteProva = props.isModels ? can(userInfo, 'DELETE_PROVA') : canEditProva;
    const canEditPrivateLabel = canEditProva && isTecnica(userInfo);
    const canSendProvaToAdmin = isHeadTecnica(userInfo);

    const castellId = prova.split('.').length > 0 ? prova.split('.')[0] : '';
    const versioName = prova.split('.').length > 1 ? prova.split('.')[1] : '';
    const [isDeleting, setIsDeleting] = useState(false);
    const deletePending = useRef(false);

    const removeFromLocalLists = () => {
        const filename = `${castellId}.${versioName}.canvis`;
        setProves(prev => ({
            ...prev,
            public: prev.public.filter(p => p !== filename),
            private: prev.private.filter(p => p !== filename),
            admin: prev.admin.filter(p => p !== filename),
        }));
        setOrder(prev => prev.filter(p => p !== filename));
    };

    const change_state_prova = () => canEditProva ? socket.emit('.change_state_prova', event, castellId, versioName, canSendProvaToAdmin) : null
    const confirm_delete_prova = () => {
        if (!canDeleteProva || deletePending.current) return;

        if (window.confirm(`Segur que vols eliminar el ${castellName}?`)) {
            deletePending.current = true;
            setIsDeleting(true);
            const finish = () => {
                deletePending.current = false;
                setIsDeleting(false);
            };
            const onError = error => {
                finish();
                if (error.status === 404) {
                    removeFromLocalLists();
                    fetchAPI(`/proves/${event}`, res => setProves(res), false);
                } else {
                    alert('No s’ha pogut eliminar la prova. Torna-ho a provar.');
                }
            };
            postAPI('/delete_prova', { event, castell: castellId, versio: versioName, userId: userInfo?.id }, res => {
                finish();
                if (res.success) {
                    removeFromLocalLists();

                    if (props.isModels || res.alreadyDeleted) {
                        // Another client may already have changed the shared order.
                        fetchAPI(`/order/${event}`, res => setOrder(res), false);
                    } else {
                        const deleted = `${castellId}.${versioName}.canvis`;
                        postOrderEdit({
                            event, edit: { op: 'remove', prova: deleted }, socket, userId: userInfo?.id,
                            onOrder: next => { if (next) setOrder(next); },
                            // Servidors d'abans de /order_edit
                            onUnsupported: () => socket.emit('.change_order', event, order.filter(prova => prova !== deleted), userInfo?.id || null),
                            onFailure: () => fetchAPI(`/order/${event}`, res => setOrder(res), false),
                        });
                    }
                } else {
                    onError(res);
                }
            }, onError);
        }
    };

    const edit_label = (e) => {
        e.preventDefault();
        if (!canEditPrivateLabel) return;
        const newLabel = prompt('Nom privat per aquesta prova:', privateLabel);
        if (newLabel === null) return;
        postAPI('/prova_label', { event, prova, label: newLabel }, () => {
            setLabels(prev => ({ ...prev, [prova]: newLabel }));
        });
    };

    const toMoment = dateStr => applyTimeZone(dateStr)
    const getName = assist => getCastellerDisplayName(assist)

    const arribenTard = assistencies
        .filter(assist => parseInt(assist['assistència']) === 1)
        .filter(assist => !isNaN(Date.parse(assist['data-entrada'])))
        .filter(assist => moment(absoluteHores[0]) < toMoment(assist['data-entrada']))
        .map(assist => [assist.id, getName(assist)])

    const surtenAviat = assistencies
        .filter(assist => parseInt(assist['assistència']) === 1 || parseInt(assist['assistència']) === 2)
        .filter(assist => !isNaN(Date.parse(assist['data-sortida'])))
        .filter(assist => toMoment(assist['data-sortida']) < moment(absoluteHores[1]))
        .map(assist => [assist.id, getName(assist)])

    const noVenen = assistencies
        .filter(assist => parseInt(assist['assistència']) === 0)
        .map(assist => [assist.id, getName(assist)])

    const [posicionsLog, setPosicionsLog] = useState(null);
    const [posicions, setPosicions] = useState({});
    const [troncJSON, setTroncJSON] = useState(null);

    useEffect(() => {
        const onLoadedJson = (json, id) => {
            if (id === castellId) {
                setTroncJSON(
                    Object.fromEntries(
                        Object.entries(json || {})
                            .filter(([id, caixa]) => caixa?.pestanya?.toLowerCase() === 'tronc' || caixa?.pilar !== undefined)
                    )
                )
            }
        }
        socket.on('.loaded_json', onLoadedJson)
        socket.emit('.load_bundle', castellId)

        // Only this prova's listener: a bare off() cut every other prova's
        return () => socket.off('.loaded_json', onLoadedJson)
    }, [castellId])

    useEffect(() => {
        if (!bundleInfo?.simultani) return;

        fetchAPI(`/events/${event}/renglesambfustesicolumnesambsim/${castellId}.${versioName}`, data => {
            const caixes_castellers = data
                .flat()
                .filter(([caixa, casteller]) => !['nom', 'fusta'].includes(caixa))
                .filter(([caixa, casteller]) => casteller in castellersInfo)
                .map(([caixa, casteller]) => [
                    {
                        pestanya: 'tronc',
                        // Other info from caixa
                    },
                    castellersInfo[casteller]
                ])
                
            setCaixesCastellers(prev => ({
                ...prev,
                [prova]: {
                    prova: bundleInfo,
                    caixesCastellers: caixes_castellers  
                }
            }))
        })
    }, [castellersInfo, prova, event, castellId, versioName, bundleInfo?.simultani])

    useEffect(() => {
        if (!posicions || !prova) return;
        setAllPosicions(prev => ({
            ...prev,
            [prova]: posicions
        }));
    }, [posicions, prova]);

    useEffect(() => {
        if (!event || !castellId || !versioName) return;

        const updatePosicions = res => {
            if ('error' in res) {
                console.log(res.error)
            } else if (res.data.split('\n').length > 0) {
                // setAjuntament(parametrizeOption('ajuntament', res.data.split('\n')[0]))
                setPosicionsLog(res.data.split('\n').slice(1))
            }
        }

        fetchAPI(`/positions/${event}/${castellId}/${versioName}`, updatePosicions)
        const onUndidAction = () => fetchAPI(`/positions/${event}/${castellId}/${versioName}`, updatePosicions)
        const onPosicionsChanged = (ev, castell, versio, pos) => {
            if (versio === versioName && castell === castellId && parseInt(ev) === parseInt(event)) {
                setPosicionsLog(prevLog => prevLog ? [...prevLog, pos] : [pos])
            }
        }
        socket.on('.undid_action', onUndidAction)
        socket.on('.posicions_changed', onPosicionsChanged)

        return () => {
            socket.off('.posicions_changed', onPosicionsChanged);
            socket.off('.undid_action', onUndidAction);
        }
    }, [event, castellId, versioName]);

    useEffect(() => {
        if (posicionsLog === null) return;
        setPosicions(rebuildPosicions({ 'posicionsLog': posicionsLog }))
    }, [posicionsLog]);

    const castellersInProva = Object.keys(posicions.castellers || {})
        .map(id => parseInt(id))

    const castellersWithRetards = [...arribenTard, ...surtenAviat]
        .filter(([id, _]) => castellersInProva.includes(id))
        .map(([id, name]) => name)

    const castellersNoVenen = noVenen
        .filter(([id, _]) => castellersInProva.includes(id))
        .map(([id, name]) => name)

    const caixaCastellerTronc = Object.keys(troncJSON || {})
        .map(caixa_id => [caixa_id, posicions?.caixes?.[caixa_id]])
        .filter(([caixa, casteller]) => casteller)
        .filter(([caixa, casteller]) => casteller in castellersInfo)
        .map(([caixa, casteller]) => [
            troncJSON[caixa],
            castellersInfo[casteller]
        ])

    useEffect(() => {
        if (bundleInfo?.simultani) return;

        setCaixesCastellers(prev => ({
            ...prev,
            [prova]: {
                prova: bundleInfo,
                caixesCastellers: caixaCastellerTronc  
            }
        }))
    }, [
        JSON.stringify(caixaCastellerTronc),
        JSON.stringify(bundleInfo),
        prova,
    ])

    // Replays the whole log: only when it changes, not on every render of the card
    const isProvaEmpty = useMemo(() => posicionsLog && (posicionsLog?.length < 2 || Object.values(rebuildPosicions({ posicionsLog }).castellers).length === 0), [posicionsLog])

    return (
        <>
            <div style={{ flex: 8, ...(props.isModels ? { minWidth: 0 } : {}) }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    { onToggleFeta && (
                        <input
                            type="checkbox"
                            className="prova-feta-check"
                            checked={feta}
                            onChange={onToggleFeta}
                            aria-label={feta ? 'Desmarca la prova com a feta' : 'Marca la prova com a feta'}
                            title={feta ? 'Feta' : 'Marca com a feta'}
                            style={{ width: 18, height: 18, margin: '0 8px 0 4px', cursor: 'pointer', accentColor: '#34a853', flexShrink: 0 }}
                        />
                    ) }
                    <div className={props.isModels ? 'model-prova-name' : undefined}
                        style={{ flex: 2, display: 'flex', alignItems: 'center', ...(props.isModels ? { minWidth: 0, flexWrap: 'wrap' } : { justifyContent: 'center', gap: 10 }), ...(feta ? { opacity: 0.55 } : {}) }}>
                        <a style={{ fontSize: 12, color: "#2859A8", ...(feta ? { textDecorationLine: 'line-through' } : {}) }} href={`/${event}/${castellId}/${versioName}/edit`}>
                            {castellDisplayName}
                        </a>
                        { feta && !onToggleFeta ? <span style={{ color: '#34a853', fontWeight: 'bold' }} aria-label="Feta">✓</span> : null }
                        {privateLabel && (
                            <span
                                style={{
                                    backgroundColor: '#e8f4fd',
                                    color: '#1976d2',
                                    borderRadius: 12,
                                    padding: '4px 10px',
                                    fontSize: 10,
                                    fontWeight: 'bold',
                                    border: '1px solid #1976d2',
                                    whiteSpace: props.isModels ? 'normal' : 'nowrap',
                                    ...(props.isModels ? { maxWidth: '100%', overflowWrap: 'anywhere', boxSizing: 'border-box' } : {})
                                }}
                            >
                                {privateLabel}
                            </span>
                        )}
                        { canEditPrivateLabel && (
                            <a href='#' onClick={edit_label} style={{ color: "#2859A8" }}>
                                &#x270E;
                            </a>
                        ) }
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            { isProvaEmpty && <span
                                style={{
                                    backgroundColor: 'rgb(94 144 224)',
                                    color: 'white',
                                    borderRadius: 5,
                                    padding: 5,
                                    fontSize: 10,
                                }}
                            >
                                Buida
                            </span> }
                        </div>
                    </div>
                    { props.isModels || editing ? (
                        <div className={props.isModels ? 'model-prova-spacer' : undefined} style={{ flex: 2, maxHeight: 55, overflow: 'auto' }} />
                    ) :
                        <div style={{ flex: 2, maxHeight: 55, overflow: 'auto' }}>
                            <div style={{ display: 'flex', height: '100%', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                                {
                                    castellersWithRetards.map(retard => {
                                        return (<div key={retard} style={{ color: 'white', backgroundColor: 'rgb(255,50,50)', borderRadius: 5, margin: 3, fontSize: 9, padding: 3 }}>
                                            🕣 {retard}
                                        </div>)
                                    })
                                }
                                {
                                    castellersNoVenen.map(no_ve => {
                                        return (<div key={no_ve} style={{ color: 'white', backgroundColor: 'rgb(255,50,50)', borderRadius: 5, margin: 3, fontSize: 9, padding: 3 }}>
                                            {no_ve}
                                        </div>)
                                    })
                                }
                            </div>
                        </div>
                    }
                    {!(props.isModels && editing) && <div className={props.isModels ? 'model-prova-actions' : undefined}
                        style={{ display: 'flex', justifyContent: 'space-evenly', ...(!props.isModels ? { flex: 3 } : {}) }}>
                        <div className="obrir-blank" style={{ display: 'flex', alignItems: 'center' }}>
                            <a style={{ textDecorationLine: 'none' }} target="_blank" href={`/${event}/${castellId}/${versioName}/edit`}>↗️ </a>
                        </div>
                        <div>
                            <button
                                style={{ backgroundColor: '#eee', border: 'solid 1px black' }}
                                onClick={change_state_prova}
                            >
                                { isAdmin ? '🛡️' : isPrivate ? '🔒' : '🌐' }
                            </button>
                        </div>
                        {
                            canDeleteProva && (props.isModels && props.modelFolders ? <ModelProvaActions models={props.modelFolders} name={prova}
                                open={props.modelMenuOpen} onToggle={props.onModelMenuToggle} label={`${castellName || castellId}${privateLabel ? ` (${privateLabel})` : ''}`} /> : (
                                <div className='eliminar-prova-boto'>
                                    <button
                                        style={{ backgroundColor: '#eee', border: 'solid 1px black' }}
                                        onClick={confirm_delete_prova}
                                        disabled={isDeleting}
                                        aria-label="Eliminar prova"
                                    >
                                        &#128465;
                                    </button>
                                </div>
                            ))
                        }
                    </div>}
                </div>

                {/* <div style={{ fontSize: 12 }}>
                    {
                        caixaCastellerTronc
                            .map(([caixa, casteller]) => `${casteller.mote} (${caixa.pilar})`)
                            .join(', ')
                    }
                </div> */}
            </div>
        </>
    );
}

export default Prova;
